import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, InputAdornment, MenuItem, Paper, Stack, Step, StepLabel, Stepper,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
  Autocomplete, CircularProgress, Alert,
} from '@mui/material';
import { CLP, getArancelPrice, getArancelDisplayPrice, getArancelBranches } from '../utils/finance';

const MODALIDADES = [
  { value: 'porcentaje', label: 'Porcentaje' },
  { value: 'fijo', label: 'Monto fijo' },
  { value: 'mixto', label: 'Mixto' },
];

const HORARIO_MODALIDADES = [
  { value: 'fijo', label: 'Horario fijo (todas las semanas)' },
  { value: 'semana_por_medio', label: 'Semana por medio' },
  { value: 'una_vez_al_mes', label: 'Una vez al mes' },
  { value: 'libre', label: 'Sin horario fijo (a demanda)' },
];

const DIAS_SEMANA = [
  { value: 'lun', label: 'Lunes' },
  { value: 'mar', label: 'Martes' },
  { value: 'mie', label: 'Miércoles' },
  { value: 'jue', label: 'Jueves' },
  { value: 'vie', label: 'Viernes' },
  { value: 'sab', label: 'Sábado' },
  { value: 'dom', label: 'Domingo' },
];

const STEPS = ['Profesional', 'Sucursal, clínica y especialidad', 'Prestaciones y montos', 'Resumen'];

const todayISO = () => new Date().toISOString().split('T')[0];

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export default function HonorariosContratoDialog({
  open,
  onClose,
  onSave,
  users,
  professionals,
  clinics,
  specialties,
  branches,
  tiposPrestacion,
  aranceles,
  initialData,
}) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1
  const [selectedUser, setSelectedUser] = useState(null);

  // Step 2
  const [validFrom, setValidFrom] = useState(todayISO());
  const [validTo, setValidTo] = useState('');
  const [notes, setNotes] = useState('');
  const [active, setActive] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState('');
  const [condiciones, setCondiciones] = useState({
    descontarIVA: false,
    tasaIVA: 19,
    descontarDescuentosComerciales: false,
    exigirPagoEnCirugias: false,
    garantiaMensual: '',
  });
  const [horarioContratado, setHorarioContratado] = useState({
    modalidad: 'fijo',
    dias: [],
    horaInicio: '',
    horaFin: '',
    montoFijoPorAsistencia: '',
  });

  // Step 3
  const [serviceRows, setServiceRows] = useState([]);
  const [searchPrestacion, setSearchPrestacion] = useState('');
  const [filterTipoPrestacionId, setFilterTipoPrestacionId] = useState('');

  // Cargar datos iniciales
  useEffect(() => {
    if (!open) return;
    if (initialData) {
      const user = users.find((u) => u.id === initialData.userId) || null;
      setSelectedUser(user);
      setValidFrom(initialData.validFrom || todayISO());
      setValidTo(initialData.validTo || '');
      setNotes(initialData.notes || '');
      setActive(initialData.active !== false);
      setSelectedBranchId(initialData.branchId || '');
      setSelectedClinicId(initialData.clinicId || '');
      setSelectedSpecialtyId(initialData.specialtyId || '');
      setCondiciones({
        descontarIVA: initialData.condiciones?.descontarIVA === true,
        tasaIVA: initialData.condiciones?.tasaIVA ?? 19,
        descontarDescuentosComerciales: initialData.condiciones?.descontarDescuentosComerciales === true,
        exigirPagoEnCirugias: initialData.condiciones?.exigirPagoEnCirugias === true,
        garantiaMensual: initialData.condiciones?.garantiaMensual ?? '',
      });
      setHorarioContratado({
        modalidad: initialData.horarioContratado?.modalidad || 'fijo',
        dias: initialData.horarioContratado?.dias || [],
        horaInicio: initialData.horarioContratado?.horaInicio || '',
        horaFin: initialData.horarioContratado?.horaFin || '',
        montoFijoPorAsistencia: initialData.horarioContratado?.montoFijoPorAsistencia ?? '',
      });
      setSearchPrestacion('');
      setFilterTipoPrestacionId('');
      if (initialData.services) {
        setServiceRows(initialData.services.map((s) => ({ ...s, _id: s.arancelId })));
      } else {
        setServiceRows([]);
      }
      setStep(0);
    } else {
      setSelectedUser(null);
      setValidFrom(todayISO());
      setValidTo('');
      setNotes('');
      setActive(true);
      setSelectedBranchId('');
      setSelectedClinicId('');
      setSelectedSpecialtyId('');
      setCondiciones({
        descontarIVA: false,
        tasaIVA: 19,
        descontarDescuentosComerciales: false,
        exigirPagoEnCirugias: false,
        garantiaMensual: '',
      });
      setHorarioContratado({
        modalidad: 'fijo',
        dias: [],
        horaInicio: '',
        horaFin: '',
        montoFijoPorAsistencia: '',
      });
      setSearchPrestacion('');
      setFilterTipoPrestacionId('');
      setServiceRows([]);
      setStep(0);
    }
  }, [open, initialData, users]);

  const selectedProfessional = useMemo(() => {
    if (!selectedUser?.professionalId) return null;
    return professionals.find((p) => p.id === selectedUser.professionalId) || null;
  }, [selectedUser, professionals]);

  const professionalSpecialties = useMemo(() => {
    if (!selectedProfessional) return [];
    const ids = selectedProfessional.specialtyIds || (selectedProfessional.specialtyId ? [selectedProfessional.specialtyId] : []);
    return specialties.filter((s) => ids.includes(s.id));
  }, [selectedProfessional, specialties]);

  const professionalClinics = useMemo(() => {
    if (!selectedProfessional) return [];
    const ids = selectedProfessional.clinicIds || (selectedProfessional.clinicId ? [selectedProfessional.clinicId] : []);
    return clinics.filter((c) => ids.includes(c.id));
  }, [selectedProfessional, clinics]);

  const selectedBranch = useMemo(
    () => branches.find((b) => b.id === selectedBranchId) || null,
    [branches, selectedBranchId],
  );

  const branchClinics = useMemo(() => {
    if (!selectedBranchId) return professionalClinics;
    return professionalClinics.filter((c) => !c.branchIds?.length || c.branchIds.includes(selectedBranchId));
  }, [professionalClinics, selectedBranchId]);

  const selectedClinic = useMemo(
    () => branchClinics.find((c) => c.id === selectedClinicId) || null,
    [branchClinics, selectedClinicId],
  );

  const selectedSpecialty = useMemo(
    () => professionalSpecialties.find((s) => s.id === selectedSpecialtyId) || null,
    [professionalSpecialties, selectedSpecialtyId],
  );

  const eligibleAranceles = useMemo(() => {
    if (!selectedClinicId || !selectedSpecialtyId) return [];
    return aranceles
      .filter((a) => a.activo !== false)
      .filter((a) => {
        const matchSpecialty = !a.specialtyId || a.specialtyId === selectedSpecialtyId;
        if (!matchSpecialty) return false;
        const arancelBranches = getArancelBranches(a);
        if (!arancelBranches.length) return true;
        if (selectedBranchId) return arancelBranches.includes(selectedBranchId);
        const clinicBranches = new Set(selectedClinic?.branchIds || []);
        if (!clinicBranches.size) return true;
        return arancelBranches.some((b) => clinicBranches.has(b));
      })
      .sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es'));
  }, [aranceles, selectedClinicId, selectedSpecialtyId, selectedBranchId, selectedClinic]);

  useEffect(() => {
    if (step !== 2) return;
    const existingMap = new Map(serviceRows.map((s) => [s.arancelId, s]));
    const next = eligibleAranceles.map((a) => {
      const prev = existingMap.get(a.id);
      return prev || {
        _id: a.id,
        arancelId: a.id,
        prestacion: a.prestacion || '',
        tipoPrestacionId: a.tipoPrestacionId || '',
        tipoPrestacionName: a.tipoPrestacionName || '',
        selected: false,
        modalidad: 'porcentaje',
        porcentaje: '',
        montoFijo: '',
      };
    });
    setServiceRows(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, eligibleAranceles.length, selectedClinicId, selectedSpecialtyId, selectedBranchId]);

  const eligibleUsers = useMemo(
    () => users.filter((u) => ['Médico Veterinario', 'Radiologo'].includes(u.role) && u.professionalId),
    [users],
  );

  const displayRows = useMemo(() => {
    const q = norm(searchPrestacion);
    return serviceRows.filter((r) => {
      const arancel = eligibleAranceles.find((a) => a.id === r.arancelId) || {};
      const text = `${arancel.prestacion || r.prestacion} ${arancel.tipoPrestacionName || r.tipoPrestacionName} ${arancel.unidadNegocioName || ''}`;
      const matchesText = !q || norm(text).includes(q);
      const matchesTipo = !filterTipoPrestacionId || r.tipoPrestacionId === filterTipoPrestacionId;
      return matchesText && matchesTipo;
    });
  }, [serviceRows, searchPrestacion, filterTipoPrestacionId, eligibleAranceles]);

  const canNext = () => {
    if (step === 0) return !!selectedProfessional;
    if (step === 1) return !!selectedBranchId && !!selectedClinicId && !!selectedSpecialtyId;
    if (step === 2) return displayRows.some((r) => r.selected) || serviceRows.some((r) => r.selected);
    return true;
  };

  const handleNext = () => {
    if (!canNext()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSave = async () => {
    if (!selectedProfessional || !selectedBranchId || !selectedClinicId || !selectedSpecialtyId) return;
    const selectedRows = serviceRows.filter((s) => s.selected);
    const services = selectedRows.map((s) => ({
      arancelId: s.arancelId,
      prestacion: s.prestacion,
      tipoPrestacionId: s.tipoPrestacionId,
      tipoPrestacionName: s.tipoPrestacionName,
      modalidad: s.modalidad,
      porcentaje: Number(s.porcentaje) || 0,
      montoFijo: Number(s.montoFijo) || 0,
    }));

    const data = {
      status: initialData?.status || 'pendiente',
      userId: selectedUser.id,
      userName: selectedUser.name || selectedUser.email || '',
      userEmail: selectedUser.email || '',
      professionalId: selectedProfessional.id,
      professionalName: `${selectedProfessional.nombre || ''} ${selectedProfessional.apellidos || ''}`.trim(),
      branchId: selectedBranchId,
      branchName: selectedBranch?.name || '',
      clinicId: selectedClinicId,
      clinicName: selectedClinic?.name || '',
      specialtyId: selectedSpecialtyId,
      specialtyName: selectedSpecialty?.name || '',
      validFrom,
      validTo,
      notes,
      active,
      condiciones: {
        descontarIVA: condiciones.descontarIVA,
        tasaIVA: Number(condiciones.tasaIVA) || 19,
        descontarDescuentosComerciales: condiciones.descontarDescuentosComerciales,
        exigirPagoEnCirugias: condiciones.exigirPagoEnCirugias,
        garantiaMensual: Number(condiciones.garantiaMensual) || 0,
      },
      horarioContratado: {
        modalidad: horarioContratado.modalidad,
        dias: horarioContratado.dias,
        horaInicio: horarioContratado.horaInicio,
        horaFin: horarioContratado.horaFin,
        montoFijoPorAsistencia: Number(horarioContratado.montoFijoPorAsistencia) || 0,
      },
      services,
    };

    setSaving(true);
    try {
      await onSave(data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const updateService = (arancelId, field, value) => {
    setServiceRows((rows) => rows.map((r) => (r.arancelId === arancelId ? { ...r, [field]: value } : r)));
  };

  const toggleAll = (checked) => {
    const visibleIds = new Set(displayRows.map((r) => r.arancelId));
    setServiceRows((rows) => rows.map((r) => (visibleIds.has(r.arancelId) ? { ...r, selected: checked } : r)));
  };

  const allSelected = displayRows.length > 0 && displayRows.every((r) => r.selected);
  const selectedCount = serviceRows.filter((s) => s.selected).length;

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Busca al usuario con rol Médico Veterinario o Radiólogo. Debe tener vinculada una ficha de profesional.
          </Typography>
          <Autocomplete
            options={eligibleUsers}
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            getOptionLabel={(u) => `${u.name || u.email || 'Usuario'} (${u.email}) — ${u.role}`}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            renderInput={(params) => (
              <TextField {...params} label="Buscar usuario" fullWidth placeholder="Nombre, email o rol" />
            )}
          />

          {selectedUser && !selectedProfessional && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Este usuario no tiene un profesional vinculado. Asigna un profesional en la configuración de usuarios.
            </Alert>
          )}

          {selectedProfessional && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {selectedProfessional.nombre} {selectedProfessional.apellidos}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                RUT: {selectedProfessional.rut || '—'} · Especialidad(es):
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                {professionalSpecialties.map((s) => (
                  <Chip key={s.id} label={s.name} size="small" color="primary" variant="outlined" />
                ))}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Clínica(s):
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {professionalClinics.map((c) => (
                  <Chip key={c.id} label={c.name} size="small" variant="outlined" />
                ))}
              </Stack>
            </Paper>
          )}
        </Box>
      );
    }

    if (step === 1) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona la sucursal, clínica y especialidad a las que aplica este contrato.
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label="Sucursal"
              value={selectedBranchId}
              onChange={(e) => {
                setSelectedBranchId(e.target.value);
                setSelectedClinicId('');
                setServiceRows([]);
              }}
            >
              <MenuItem value=""><em>Seleccionar sucursal</em></MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="Clínica"
              value={selectedClinicId}
              onChange={(e) => {
                setSelectedClinicId(e.target.value);
                setServiceRows([]);
              }}
              disabled={!selectedBranchId}
            >
              <MenuItem value=""><em>Seleccionar clínica</em></MenuItem>
              {branchClinics.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="Especialidad"
              value={selectedSpecialtyId}
              onChange={(e) => {
                setSelectedSpecialtyId(e.target.value);
                setServiceRows([]);
              }}
            >
              <MenuItem value=""><em>Seleccionar especialidad</em></MenuItem>
              {professionalSpecialties.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                label="Vigente desde"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label="Vigente hasta (opcional)"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>

            <TextField
              multiline
              rows={2}
              label="Notas del contrato"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <FormControlLabel
              control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} color="success" />}
              label={active ? 'Contrato activo' : 'Contrato inactivo'}
            />

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Horario contratado
            </Typography>
            <TextField
              select
              fullWidth
              label="Modalidad de horario"
              value={horarioContratado.modalidad}
              onChange={(e) => setHorarioContratado((h) => ({ ...h, modalidad: e.target.value }))}
            >
              {HORARIO_MODALIDADES.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>

            {horarioContratado.modalidad !== 'libre' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Días de la semana
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {DIAS_SEMANA.map((d) => {
                    const checked = horarioContratado.dias.includes(d.value);
                    return (
                      <Chip
                        key={d.value}
                        label={d.label}
                        size="small"
                        color={checked ? 'primary' : 'default'}
                        variant={checked ? 'filled' : 'outlined'}
                        onClick={() => setHorarioContratado((h) => ({
                          ...h,
                          dias: checked ? h.dias.filter((x) => x !== d.value) : [...h.dias, d.value],
                        }))}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )}

            {horarioContratado.modalidad !== 'libre' && (
              <Stack direction="row" spacing={2}>
                <TextField
                  type="time"
                  label="Hora inicio"
                  value={horarioContratado.horaInicio}
                  onChange={(e) => setHorarioContratado((h) => ({ ...h, horaInicio: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="time"
                  label="Hora fin"
                  value={horarioContratado.horaFin}
                  onChange={(e) => setHorarioContratado((h) => ({ ...h, horaFin: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            )}

            <TextField
              type="number"
              label="Monto fijo por asistencia ($)"
              value={horarioContratado.montoFijoPorAsistencia}
              onChange={(e) => setHorarioContratado((h) => ({ ...h, montoFijoPorAsistencia: e.target.value }))}
              helperText="Monto fijo que se paga cada vez que el profesional asiste, independiente de las prestaciones"
              fullWidth
            />

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Condiciones comerciales de la liquidación
            </Typography>

            <FormControlLabel
              control={<Switch checked={condiciones.descontarIVA} onChange={(e) => setCondiciones((c) => ({ ...c, descontarIVA: e.target.checked }))} />}
              label="Descontar IVA antes de aplicar el porcentaje"
            />

            <TextField
              type="number"
              label="Tasa IVA (%)"
              value={condiciones.tasaIVA}
              onChange={(e) => setCondiciones((c) => ({ ...c, tasaIVA: e.target.value }))}
              disabled={!condiciones.descontarIVA}
              fullWidth
            />

            <FormControlLabel
              control={<Switch checked={condiciones.descontarDescuentosComerciales} onChange={(e) => setCondiciones((c) => ({ ...c, descontarDescuentosComerciales: e.target.checked }))} />}
              label="Descontar descuentos comerciales (proporcional)"
            />

            <FormControlLabel
              control={<Switch checked={condiciones.exigirPagoEnCirugias} onChange={(e) => setCondiciones((c) => ({ ...c, exigirPagoEnCirugias: e.target.checked }))} />}
              label="Exigir que el presupuesto esté pagado para liquidar cirugías"
            />

            <TextField
              type="number"
              label="Garantía mensual ($)"
              value={condiciones.garantiaMensual}
              onChange={(e) => setCondiciones((c) => ({ ...c, garantiaMensual: e.target.value }))}
              helperText="Si la liquidación del contrato queda bajo este monto, se completa con un ajuste"
              fullWidth
            />
          </Stack>
        </Box>
      );
    }

    if (step === 2) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Selecciona las prestaciones del arancel que participarán y define el monto a pagar.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{selectedBranch?.name}</strong> · <strong>{selectedClinic?.name}</strong> · <strong>{selectedSpecialty?.name}</strong>
            {eligibleAranceles.length > 0 && ` · ${eligibleAranceles.length} prestaciones encontradas`}
          </Typography>

          {eligibleAranceles.length === 0 ? (
            <Alert severity="warning">
              No se encontraron prestaciones del arancel para esta combinación.
            </Alert>
          ) : (
            <>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Buscar prestación"
                  value={searchPrestacion}
                  onChange={(e) => setSearchPrestacion(e.target.value)}
                  sx={{ minWidth: 260 }}
                />
                <TextField
                  select
                  size="small"
                  label="Filtrar por tipo"
                  value={filterTipoPrestacionId}
                  onChange={(e) => setFilterTipoPrestacionId(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value=""><em>Todos los tipos</em></MenuItem>
                  {tiposPrestacion.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="text.secondary">
                  {displayRows.length} vista(s) · {selectedCount} seleccionada(s)
                </Typography>
              </Stack>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={allSelected} onChange={(e) => toggleAll(e.target.checked)} />
                      </TableCell>
                      <TableCell><strong>Prestación</strong></TableCell>
                      <TableCell><strong>Tipo</strong></TableCell>
                      <TableCell align="right"><strong>Precio ref.</strong></TableCell>
                      <TableCell sx={{ minWidth: 130 }}><strong>Modalidad</strong></TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}><strong>%</strong></TableCell>
                      <TableCell align="right" sx={{ minWidth: 140 }}><strong>Monto fijo</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayRows.map((row) => {
                      const arancel = eligibleAranceles.find((a) => a.id === row.arancelId) || {};
                      const price = selectedBranchId ? getArancelPrice(arancel, selectedBranchId) : getArancelDisplayPrice(arancel);
                      return (
                        <TableRow key={row.arancelId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={row.selected}
                              onChange={(e) => updateService(row.arancelId, 'selected', e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{arancel.prestacion || row.prestacion}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {arancel.unidadNegocioName || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{arancel.tipoPrestacionName || row.tipoPrestacionName || '—'}</TableCell>
                          <TableCell align="right">{CLP(price)}</TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              value={row.modalidad}
                              onChange={(e) => updateService(row.arancelId, 'modalidad', e.target.value)}
                              fullWidth
                            >
                              {MODALIDADES.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={row.porcentaje}
                              disabled={row.modalidad === 'fijo'}
                              onChange={(e) => updateService(row.arancelId, 'porcentaje', e.target.value)}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={row.montoFijo}
                              disabled={row.modalidad === 'porcentaje'}
                              onChange={(e) => updateService(row.arancelId, 'montoFijo', e.target.value)}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              fullWidth
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      );
    }

    // Step 3 - Resumen
    const selectedServices = serviceRows.filter((s) => s.selected);
    return (
      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Resumen del contrato
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Typography variant="body1">
              <strong>Profesional:</strong> {selectedProfessional?.nombre} {selectedProfessional?.apellidos}
            </Typography>
            <Typography variant="body1">
              <strong>Usuario:</strong> {selectedUser?.name || selectedUser?.email} ({selectedUser?.email})
            </Typography>
            <Typography variant="body1">
              <strong>Sucursal:</strong> {selectedBranch?.name} · <strong>Clínica:</strong> {selectedClinic?.name} · <strong>Especialidad:</strong> {selectedSpecialty?.name}
            </Typography>
            <Typography variant="body1">
              <strong>Vigencia:</strong> {validFrom || '—'} {validTo ? `hasta ${validTo}` : 'sin fecha de término'}
            </Typography>
            <Typography variant="body1">
              <strong>Notas:</strong> {notes || '—'}
            </Typography>
            <Typography variant="body1">
              <strong>Horario contratado:</strong> {(() => {
                const h = horarioContratado;
                const mod = HORARIO_MODALIDADES.find((m) => m.value === h.modalidad)?.label || h.modalidad;
                if (h.modalidad === 'libre') return mod;
                const dias = (h.dias || []).map((d) => DIAS_SEMANA.find((x) => x.value === d)?.label || d).join(', ');
                const horario = h.horaInicio && h.horaFin ? `${h.horaInicio} a ${h.horaFin}` : 'sin horario definido';
                return `${mod} · ${dias || 'sin días'} · ${horario}`;
              })()}
            </Typography>
            <Typography variant="body1">
              <strong>Monto fijo por asistencia:</strong> {horarioContratado.montoFijoPorAsistencia ? CLP(Number(horarioContratado.montoFijoPorAsistencia)) : '—'}
            </Typography>
            <Typography variant="body1">
              <strong>Servicios incluidos:</strong> {selectedServices.length}
            </Typography>
          </Stack>
        </Paper>

        {selectedServices.length === 0 ? (
          <Alert severity="warning">No has seleccionado servicios para este contrato.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Prestación</strong></TableCell>
                  <TableCell><strong>Tipo</strong></TableCell>
                  <TableCell><strong>Modalidad</strong></TableCell>
                  <TableCell align="right"><strong>%</strong></TableCell>
                  <TableCell align="right"><strong>Monto fijo</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedServices.map((s) => (
                  <TableRow key={s.arancelId} hover>
                    <TableCell>{s.prestacion}</TableCell>
                    <TableCell>{s.tipoPrestacionName || '—'}</TableCell>
                    <TableCell>
                      <Chip label={s.modalidad} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{s.modalidad === 'fijo' ? '—' : `${s.porcentaje}%`}</TableCell>
                    <TableCell align="right">{s.modalidad === 'porcentaje' ? '—' : CLP(s.montoFijo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {initialData ? 'Editar contrato de honorarios' : 'Nuevo contrato de honorarios'}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {step > 0 && (
          <Button onClick={handleBack} variant="outlined" disabled={saving}>
            Atrás
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button onClick={handleNext} variant="contained" disabled={!canNext() || saving}>
            {step === 2 ? 'Ver resumen' : 'Siguiente'}
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || selectedCount === 0}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {initialData ? 'Guardar cambios' : 'Crear contrato'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
